import { gql } from '@apollo/client';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconRocket from '@apollo/icons/default/IconRocket.svg';
import IconSatellite1 from '@apollo/icons/default/IconSatellite1.svg';
import IconVariant from '@apollo/icons/default/IconVariant.svg';
import { Heading, Link, Tooltip } from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { ContractBadge } from 'src/app/appLayout/components/globalHeaderGraphContent/contractGraphBadge/ContractGraphBadge';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import {
  launchesRouteConfig,
  variantHomepageRouteConfig,
} from 'src/app/graph/routes';
import { committerDisplayName } from 'src/app/shared/commiterDisplayName';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const contractDetailsHeaderFragment = gql`
  fragment ContractDetailsHeaderFragment on Service {
    id
    variant(name: $variant) {
      id
      url
      permissions {
        canQueryBuildConfig
        canManageExplorerSettings
      }
      sourceVariant {
        id
        name
      }
      latestApprovedLaunch {
        id
      }
      activeSchemaPublish {
        id
        publishedAt
        schema {
          createdAt
          hash
        }
        gitContext {
          committer
        }
      }
    }
  }
`;
interface Props {
  headerData: GraphQLTypes.ContractDetailsModalQuery_service;
  graphRef: GraphRef;
}

export const ContractDetailsHeader = ({ headerData, graphRef }: Props) => {
  const location = useLocation();
  const createdAt = headerData.variant?.activeSchemaPublish?.schema.createdAt
    ? new Date(headerData.variant.activeSchemaPublish.schema.createdAt)
    : null;
  const lastUpdated = headerData.variant?.activeSchemaPublish?.publishedAt
    ? new Date(headerData.variant?.activeSchemaPublish?.publishedAt)
    : null;

  const version = headerData.variant?.activeSchemaPublish?.schema.hash;
  const committerName = committerDisplayName(
    headerData.variant?.activeSchemaPublish?.gitContext?.committer || '',
  );
  const lastUpdateTimestamp = lastUpdated ?? createdAt;

  return (
    <div className="max-w-full flex-auto shrink">
      <div className="mt-2 flex items-center justify-between">
        <Tooltip
          label={`👈 This is the Graph Ref. Use it to call this graph@variant in Rover. (Apollo's CLI tool 🤖)`}
          placement="right"
        >
          <Heading
            as="h2"
            size="2xl"
            className="max-w-sm truncate"
            title={`${graphRef.graphId}@${graphRef.graphVariant}`}
          >
            {graphRef.graphId}@{graphRef.graphVariant}
          </Heading>
        </Tooltip>
        <ContractBadge
          graphId={graphRef.graphId}
          graphVariant={graphRef.graphVariant}
        />
      </div>
      <div className="mt-4 flex items-center text-base">
        <div>
          {/* Connection Settings */}
          <div className="flex items-center">
            <IconSatellite1 className="mr-2" />
            {headerData.variant?.url ? (
              <span className="truncate">{headerData.variant?.url}</span>
            ) : (
              <span>
                Graph endpoint not configured
                {headerData.variant?.permissions.canManageExplorerSettings && (
                  <span>
                    {' '}
                    - add one in{' '}
                    <Link
                      to={{
                        ...setOverlayVisibleLocation({
                          location,
                          overlayName: Config.modals.connectionSettings,
                        }),
                        state: {
                          linkEndpointModalData: {
                            openingIntent: 'Set url',
                          },
                        },
                      }}
                    >
                      Connection Settings
                    </Link>
                  </span>
                )}
              </span>
            )}
          </div>
          {/* History Section */}
          {lastUpdateTimestamp && (
            <div className="mt-1.5 flex items-center">
              <IconHistory />
              <span
                className={classnames(
                  'ml-2',
                  committerName && committerName !== 'Unknown'
                    ? 'mr-1'
                    : 'mr-2',
                )}
              >
                <LocalTimestamp value={lastUpdateTimestamp} />
              </span>
              {committerName && committerName !== 'Unknown' && (
                <span>
                  {` by `}
                  <span className="mr-2 font-semibold">{committerName}</span>
                </span>
              )}
              {version && (
                <li className="text-sm">
                  <span className="-ml-1">
                    {version && version.substr(0, 6)}
                  </span>
                </li>
              )}
            </div>
          )}
          {/* Source Variant */}
          {headerData.variant?.permissions.canQueryBuildConfig && (
            <div className="mt-1.5 flex items-center">
              <IconVariant />
              <div className="ml-2">
                Sourced from
                <Link
                  to={variantHomepageRouteConfig.location({
                    graphId: graphRef.graphId,
                    graphVariant: headerData.variant?.sourceVariant
                      ?.name as string,
                  })}
                >
                  <span
                    className="ml-1"
                    title={`${headerData.id}@${headerData.variant?.sourceVariant?.name}`}
                  >
                    {headerData.id}@{headerData.variant?.sourceVariant?.name}
                  </span>
                </Link>
              </div>
            </div>
          )}
          {/* Launch Details */}
          {headerData.variant?.latestApprovedLaunch && (
            <Tooltip label="The latest successful launch" placement="top-start">
              <div className="mt-1.5 flex items-center">
                <IconRocket />
                <span className="ml-2">
                  <Link
                    className="ml-0.5 hover:underline"
                    to={launchesRouteConfig.location({
                      ...graphRef,
                      launchId: headerData.variant.latestApprovedLaunch.id,
                    })}
                  >
                    {headerData.variant?.latestApprovedLaunch.id.substr(0, 8)}
                  </Link>
                </span>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
